/**
 * The last filter before a console line leaves the process as a Sentry log.
 *
 * `consoleLoggingIntegration` ships a `console.warn` / `console.error` call's
 * arguments as they are, and nothing at the call sites bounds what they
 * interpolate the way `sanitizeExtra` bounds a report's `extra`. A provider's
 * error text can quote a recipient's address; a stack can carry a URL with a
 * token in it. This masks the shapes that identify a person or open a door,
 * in the message and in every attribute, so the identifiers-only rule of
 * `context.ts` holds for logs too. Attributes are walked, not just read: the
 * integration stores each console argument after the first, an error object
 * included, as a normalised object under `sentry.message.parameter.<n>`.
 *
 * ⚠️ A mask, not a guarantee: it removes the shapes below and nothing else.
 * A call site still must not log a name, an address or a message body.
 */

import type { Log } from "@sentry/nextjs";

const MASKS: ReadonlyArray<readonly [RegExp, string]> = [
	// email addresses
	[/[\w.%+-]+@[\w-]+(?:\.[\w-]+)+/g, "[email]"],
	// an Authorization value, and a bearer/JWT-shaped token anywhere
	[/\b(Bearer|Basic)\s+[\w.~+/=-]+/gi, "$1 [token]"],
	[/\beyJ[\w-]+\.[\w-]+\.[\w-]+/g, "[jwt]"],
	// secrets in a query string
	[/([?&](?:token|secret|code|key|signature|sig|password)=)[^&\s"']+/gi, "$1[redacted]"],
	// a phone number: `+` and 7+ digits (E.164, how Jana stores one), or 8+ bare digits
	[/\+\d[\d\s-]{6,}\d/g, "[phone]"],
	[/(?<![\w.-])\d{8,15}(?![\w.-])/g, "[number]"],
];

export function scrubText(text: string): string {
	let out = text;
	for (const [pattern, replacement] of MASKS) out = out.replace(pattern, replacement);
	return out;
}

/** Masks every string inside `value`. The integration has already bounded its depth. */
function scrubValue(value: unknown, depth = 0): unknown {
	if (typeof value === "string") return scrubText(value);
	if (value === null || typeof value !== "object" || depth > 8) return value;
	if (Array.isArray(value)) return value.map((v) => scrubValue(v, depth + 1));
	const out: Record<string, unknown> = {};
	for (const [key, v] of Object.entries(value)) out[key] = scrubValue(v, depth + 1);
	return out;
}

/** `beforeSendLog` for every runtime. Never drops a log, only masks it. */
export function scrubLog(log: Log, extraAttributes?: Record<string, string>): Log {
	const attributes: Record<string, unknown> = { ...extraAttributes };
	for (const [key, value] of Object.entries(log.attributes ?? {}))
		attributes[key] = scrubValue(value);
	return { ...log, message: scrubText(String(log.message)), attributes };
}
